import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, LayoutGrid } from "lucide-react";
import { listWellsides, listProducts } from "@/admin/adminApi";
import { CATEGORY_CONFIGS } from "@/admin/categoryConfig";
import { useCategoryOrder, sortBySlug } from "@/hooks/useCategoryOrder";

const CARDS = [
  { slug: "wellside-canopies", label: "Wellside Canopies", fetchCount: () => listWellsides().then((d) => d.length) },
  ...Object.entries(CATEGORY_CONFIGS).map(([slug, cfg]) => ({
    slug,
    label: cfg.label,
    fetchCount: () => listProducts(cfg.category).then((d) => d.length),
  })),
];

export default function AdminDashboardPage() {
  const [counts, setCounts] = useState({});
  const order = useCategoryOrder();
  const cards = useMemo(() => sortBySlug(CARDS, order, (c) => c.slug), [order]);

  useEffect(() => {
    CARDS.forEach((card) => {
      card.fetchCount().then((count) => setCounts((prev) => ({ ...prev, [card.slug]: count })));
    });
  }, []);

  return (
    <div data-testid="admin-dashboard-page">
      <div className="flex items-center gap-2 mb-2">
        <LayoutGrid size={20} className="text-[var(--brand-primary)]" />
        <h1 className="font-display text-3xl text-white">Product Categories</h1>
      </div>
      <p className="text-white/50 text-sm font-mono mb-8">Pick a category to manage its products, photos and pricing.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card) => (
          <Link
            key={card.slug}
            to={`/admin/${card.slug}`}
            data-testid={`admin-dashboard-card-${card.slug}`}
            className="group border border-[var(--border-default)] bg-[var(--bg-secondary)] p-6 hover:border-[var(--brand-primary)] transition-colors"
          >
            <div className="flex items-start justify-between">
              <h2 className="font-display text-xl text-white">{card.label}</h2>
              <ArrowUpRight size={18} className="text-white/40 group-hover:text-[var(--brand-primary)] transition-colors" />
            </div>
            <p className="text-white/50 text-sm font-mono mt-2">
              {counts[card.slug] == null ? "Loading…" : `${counts[card.slug]} product${counts[card.slug] === 1 ? "" : "s"}`}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}
